'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

interface SignupFormData {
  firstName: string;
  lastName: string;
  company: string;
  jobTitle: string;
  primaryRegion: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

const regions = [
  'Global (Multi-region)', 'North America', 'Europe & UK',
  'Middle East & North Africa', 'Asia Pacific', 'Sub-Saharan Africa',
  'Latin America', 'Central Asia',
];

const jobTitles = [
  'Chief Risk Officer', 'VP / Head of Risk', 'Risk Analyst',
  'Supply Chain Manager', 'Compliance Officer', 'Intelligence Analyst',
  'Finance / Treasury', 'Consulting / Advisory', 'Other',
];

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { setUser } = useAuth();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>();
  const passwordValue = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1200));

    // Create a new user session (viewer role for self-registered users)
    const newUser = {
      id: `user-${Date.now()}`,
      email: data.email,
      name: `${data.firstName} ${data.lastName}`,
      role: 'viewer' as const,
      company: data.company,
      initials: `${data.firstName[0]}${data.lastName[0]}`.toUpperCase(),
    };

    setUser(newUser);
    setIsLoading(false);
    setSubmitted(true);
    toast.success('Account created', {
      description: `Welcome to GlobalAtlas, ${data.firstName}. Redirecting to dashboard…`,
    });
    await new Promise(r => setTimeout(r, 1000));
    window.location.href = '/ai-risk-dashboard';
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center text-center py-8 animate-slide-up">
        <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <CheckCircle2 size={28} className="text-success" />
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Account created</h2>
        <p className="text-sm text-muted-foreground">Redirecting you to the Risk Dashboard…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div>
        <h1 className="text-xl font-semibold text-foreground mb-1">Create your account</h1>
        <p className="text-sm text-muted-foreground">Start your 14-day free trial — no credit card required</p>
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="signup-firstName" className="block text-xs font-medium text-foreground mb-1.5">First Name <span className="text-danger">*</span></label>
          <input id="signup-firstName" type="text" autoComplete="given-name" placeholder="Marcus"
            className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.firstName ? 'border-danger' : 'border-border'}`}
            {...register('firstName', { required: 'Required' })} />
          {errors.firstName && <p className="mt-1 text-2xs text-danger">{errors.firstName.message}</p>}
        </div>
        <div>
          <label htmlFor="signup-lastName" className="block text-xs font-medium text-foreground mb-1.5">Last Name <span className="text-danger">*</span></label>
          <input id="signup-lastName" type="text" autoComplete="family-name" placeholder="Klein"
            className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.lastName ? 'border-danger' : 'border-border'}`}
            {...register('lastName', { required: 'Required' })} />
          {errors.lastName && <p className="mt-1 text-2xs text-danger">{errors.lastName.message}</p>}
        </div>
      </div>

      {/* Company */}
      <div>
        <label htmlFor="signup-company" className="block text-xs font-medium text-foreground mb-1.5">Company <span className="text-danger">*</span></label>
        <input id="signup-company" type="text" autoComplete="organization" placeholder="Acme Corporation"
          className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.company ? 'border-danger' : 'border-border'}`}
          {...register('company', { required: 'Company name is required' })} />
        {errors.company && <p className="mt-1 text-xs text-danger">{errors.company.message}</p>}
      </div>

      {/* Job title + Region */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="signup-jobTitle" className="block text-xs font-medium text-foreground mb-1.5">Job Title <span className="text-danger">*</span></label>
          <select id="signup-jobTitle"
            className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors appearance-none cursor-pointer ${errors.jobTitle ? 'border-danger' : 'border-border'}`}
            {...register('jobTitle', { required: 'Required' })}>
            <option value="">Select role</option>
            {jobTitles.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {errors.jobTitle && <p className="mt-1 text-2xs text-danger">{errors.jobTitle.message}</p>}
        </div>
        <div>
          <label htmlFor="signup-region" className="block text-xs font-medium text-foreground mb-1.5">Primary Region <span className="text-danger">*</span></label>
          <select id="signup-region"
            className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors appearance-none cursor-pointer ${errors.primaryRegion ? 'border-danger' : 'border-border'}`}
            {...register('primaryRegion', { required: 'Required' })}>
            <option value="">Select region</option>
            {regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {errors.primaryRegion && <p className="mt-1 text-2xs text-danger">{errors.primaryRegion.message}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="signup-email" className="block text-xs font-medium text-foreground mb-1.5">Work Email <span className="text-danger">*</span></label>
        <input id="signup-email" type="email" autoComplete="email" placeholder="you@company.com"
          className={`w-full bg-input border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.email ? 'border-danger' : 'border-border'}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })} />
        {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="signup-password" className="block text-xs font-medium text-foreground mb-1.5">Password <span className="text-danger">*</span></label>
        <div className="relative">
          <input id="signup-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Create a strong password"
            className={`w-full bg-input border rounded-md px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.password ? 'border-danger' : 'border-border'}`}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Minimum 8 characters' },
            })} />
          <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="signup-confirm" className="block text-xs font-medium text-foreground mb-1.5">Confirm Password <span className="text-danger">*</span></label>
        <div className="relative">
          <input id="signup-confirm" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" placeholder="Repeat your password"
            className={`w-full bg-input border rounded-md px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: v => v === passwordValue || 'Passwords do not match',
            })} />
          <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2">
        <input id="accept-terms" type="checkbox" className="w-3.5 h-3.5 accent-primary rounded mt-0.5"
          {...register('acceptTerms', { required: 'You must accept the terms' })} />
        <label htmlFor="accept-terms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
          I agree to the <span className="text-primary">Terms of Service</span> and <span className="text-primary">Privacy Policy</span>
        </label>
      </div>
      {errors.acceptTerms && <p className="text-xs text-danger">{errors.acceptTerms.message}</p>}

      <button type="submit" disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
        style={{ minHeight: '42px' }}>
        {isLoading ? (<><Loader2 size={15} className="animate-spin" /><span>Creating account…</span></>) : 'Create Account'}
      </button>

      <p className="text-center text-xs text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Sign in
        </button>
      </p>
    </form>
  );
}